@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.keraunic

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color

import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.pages.probes.PdfBlockedNotice
import com.nonopn.skydome.common.app.pages.probes.rememberProbeAccess
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryType
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportData
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportText
import com.nonopn.skydome.common.app.pages.pdf.PdfKeraunicBuilder
import com.nonopn.skydome.common.app.pages.pdf.PdfLangPickerDialog
import com.nonopn.skydome.common.app.pages.pdf.PdfOutputLang
import com.nonopn.skydome.common.app.pages.pdf.SvgMapBuilder
import com.nonopn.skywatcher.maps.MapUtils
import com.nonopn.skywatcher.maps.providers.GoogleProvider
import com.nonopn.skydome.common.app.pages.fullscreenmap.FullscreenMapContainer
import com.nonopn.skydome.common.app.pages.fullscreenmap.MapZoomControls
import ovh.plrapps.mapcompose.api.addLayer
import ovh.plrapps.mapcompose.api.addMarker
import ovh.plrapps.mapcompose.api.addPath
import ovh.plrapps.mapcompose.api.scale
import ovh.plrapps.mapcompose.api.snapScrollTo
import ovh.plrapps.mapcompose.core.TileStreamProvider
import ovh.plrapps.mapcompose.ui.MapUI
import ovh.plrapps.mapcompose.ui.state.MapState
import com.nonopn.skydome.common.app.pages.sharing.createShareManager
import com.nonopn.skywatcher.maps.LatLng

import eu.codlab.viewmodel.rememberViewModel

import com.nonopn.skydome.common.app.utils.getSystemLanguage

import korlibs.time.DateTime

import kotlinx.coroutines.launch

import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.min
import org.jetbrains.compose.resources.stringResource
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.keraunic_report_title_label
import com.nonopn.skywatcher.common.app.res.keraunic_retry
import com.nonopn.skywatcher.common.app.res.cert_add_observations
import com.nonopn.skywatcher.common.app.res.keraunic_export_pdf
import com.nonopn.skywatcher.common.app.res.report_keraunic_title
import com.nonopn.skywatcher.common.app.res.keraunic_screen_loading_error
import com.nonopn.skywatcher.common.app.res.keraunic_screen_loading_error_body
import com.nonopn.skywatcher.common.app.res.keraunic_screen_free_observations

private const val MAP_LEVEL_COUNT = 18
private val MAP_FULL_SIZE = 256 shl MAP_LEVEL_COUNT

private object Colors {
    val Primary = Color(0xFF2B7FE0)
    val Text = Color(0xFF1A2B4A)
    val Background = Color(0xFFF5F7FA)
    val Card = Color(0xFFEEF2F8)
    val Positive = Color(0xFFE63B2E) // red = CG+ (positive)
    val Negative = Color(0xFF2B7FE0) // blue = CG- (negative)
    val GreenLight = Color(0xFFEEF7F0)
    val BlueLight = Color(0xFFF0F6FF)
    val BlueBorder = Color(0xFFC5D9F5)
    val MapBackground = Color(0xFFFAFBFC)
    val Amber = Color(0xFFF5A800)
    val SecondaryText = Color(0xFF6B7A99)
}

@Suppress("LongMethod")
@Composable
fun KeraunicReportScreen(
    project: Project,
    appModel: AppModel
) {
    val appState by appModel.states.collectAsState()
    val api = appState.apiProvider?.apiNormal
    val model = rememberViewModel { KeraunicReportModel(project, api) }
    val state by model.states.collectAsState()
    val scroll = rememberScrollState()
    val shareManager = remember { createShareManager() }

    Column(
        Modifier
            .fillMaxSize()
            .background(Colors.Background)
            .verticalScroll(scroll)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // 1. Header section
        SectionCard {
            Column(
                Modifier.fillMaxWidth().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    stringResource(Res.string.report_keraunic_title),
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = Colors.Text
                )
                OutlinedTextField(
                    value = state.title,
                    onValueChange = { model.setTitle(it) },
                    label = { Text(stringResource(Res.string.keraunic_report_title_label)) },
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }

        if (state.loading) {
            Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = Colors.Primary)
            }
            return@Column
        }

        if (state.error) {
            SectionCard {
                Column(
                    Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text(
                        stringResource(Res.string.keraunic_screen_loading_error),
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                        color = Colors.Positive,
                    )
                    Text(
                        stringResource(Res.string.keraunic_screen_loading_error_body),
                        fontSize = 13.sp,
                        color = Colors.SecondaryText,
                    )
                    Button(
                        onClick = { model.load() },
                        colors = ButtonDefaults.buttonColors(containerColor = Colors.Primary)
                    ) {
                        Text(stringResource(Res.string.keraunic_retry), color = Color.White)
                    }
                }
            }
            return@Column
        }

        val data = state.data ?: return@Column
        val text = remember { KeraunicReportText.forLanguage(PdfOutputLang.fromCode(getSystemLanguage())) }

        // PDF page 1 — cover
        KeraunicCoverSection(data, text, project.name)

        // PDF page 2 — notable impacts, Nsg of the nearest cities, detection network
        KeraunicOverviewSection(data, text)

        // PDF page 3 — impact map
        ImpactMapSection(
            projectLat = project.latitude,
            projectLon = project.longitude,
            impacts = data.mapImpacts,
            data = data,
            text = text,
        )

        // PDF pages 4-5 — zone analysis
        KeraunicZoneSection(data.zone1, isDirect = true, keyPoints = data.directTopImpacts, text = text)
        KeraunicZoneSection(data.zone2, isDirect = false, keyPoints = emptyList(), text = text)

        // PDF page 6 — possible effects & wider context
        KeraunicEffectsSection(data, text)

        // PDF pages 7-10 — monthly activity and intensity, hourly distribution and intensity
        KeraunicMonthlyActivitySection(data, text)
        KeraunicMonthlyIntensitySection(data, text)
        KeraunicHourlyDistributionSection(data.hourly, text)
        KeraunicHourlyIntensitySection(data.hourly, text)

        // PDF page 11 — risk score and action plan
        KeraunicRecommendationsSection(data, text)

        // Free observations (PDF page 12, before the methodology)
        SectionCard {
            Column(
                Modifier.fillMaxWidth().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                SectionTitle(stringResource(Res.string.keraunic_screen_free_observations))
                OutlinedTextField(
                    value = state.observations,
                    onValueChange = { model.setObservations(it) },
                    label = { Text(stringResource(Res.string.cert_add_observations)) },
                    modifier = Modifier.fillMaxWidth().heightIn(min = 100.dp),
                    maxLines = 8,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text)
                )
            }
        }

        // PDF page 12 — methodology & sources (the observations above are printed just before it in the PDF)
        KeraunicMethodologySection(text)

        // PDF export with language picker
        var showLangPicker by remember { mutableStateOf(false) }
        // PDF exports need a paid probe when only trial probes cover the site
        val probeAccess = rememberProbeAccess(appModel, project.latitude, project.longitude)
        if (probeAccess.pdfBlocked) {
            PdfBlockedNotice(Modifier.padding(bottom = 8.dp))
        }

        Button(
            modifier = Modifier.fillMaxWidth().height(48.dp),
            onClick = { showLangPicker = true },
            enabled = !probeAccess.pdfBlocked,
            colors = ButtonDefaults.buttonColors(containerColor = Colors.Primary)
        ) {
            Text(stringResource(Res.string.keraunic_export_pdf), color = Color.White, fontWeight = FontWeight.Bold)
        }

        if (showLangPicker) {
            PdfLangPickerDialog(
                currentLang = PdfOutputLang.fromCode(getSystemLanguage()),
                onLangSelected = { lang ->
                    showLangPicker = false
                    val now = DateTime.now().local
                    val generatedDate = "${now.dayOfMonth.toString().padStart(2, '0')}/" +
                        "${now.month1.toString().padStart(2, '0')}/${now.yearInt}"
                    val html = PdfKeraunicBuilder.build(
                        lang, project.name, data, generatedDate,
                        customTitle = KeraunicReportModel.customTitle(state.title, project.name),
                        observations = state.observations,
                    )
                    shareManager.shareHtmlAsPdf(state.title, html)
                    val nowMillis = DateTime.now().unixMillisLong
                    appModel.recordDocumentHistory(
                        DocumentHistoryItem(
                            id = "keraunic_client_${project.uuid}_$nowMillis",
                            type = DocumentHistoryType.KERAUNIC_REPORT,
                            siteName = state.title.ifBlank { project.name },
                            latitude = project.latitude,
                            longitude = project.longitude,
                            createdAtSecondsUtc = nowMillis / 1000L,
                        )
                    )
                },
                onDismiss = { showLangPicker = false },
            )
        }

        Spacer(Modifier.height(24.dp))
    }
}

@Composable
private fun ImpactMapSection(
    projectLat: Double,
    projectLon: Double,
    impacts: List<SvgMapBuilder.ImpactDot>,
    data: KeraunicReportData,
    text: KeraunicReportText,
) {
    val googleApi = remember { GoogleProvider() }
    val tileStreamProvider = remember(googleApi) {
        TileStreamProvider { row, col, zoomLvl ->
            googleApi.getTile(row, col, zoomLvl)
        }
    }
    val center = remember { MapUtils.latLngToXY(projectLat, projectLon) }
    val mapSizeDp = 250.dp
    val density = LocalDensity.current
    val initialScale = remember {
        val vpPx = with(density) { mapSizeDp.toPx() }
        val cosLat = cos(projectLat * PI / 180.0)
        val xRange = 2.0 * 2.6 / (40075.0 * cosLat)
        vpPx / (MAP_FULL_SIZE.toDouble() * xRange)
    }
    val mapState = remember {
        MapState(
            levelCount = MAP_LEVEL_COUNT,
            fullWidth = MAP_FULL_SIZE,
            fullHeight = MAP_FULL_SIZE,
            tileSize = 256,
        ) {
            scroll(center.x, center.y)
        }.apply {
            scale = initialScale
        }
    }
    val scope = rememberCoroutineScope()
    LaunchedEffect(Unit) {
        try {
            googleApi.initialize()
        } catch (_: Throwable) { }
        try {
            mapState.addLayer(tileStreamProvider)
        } catch (_: Throwable) { }
        // 1 km ring
        try {
            mapState.addPath(
                id = "ring_1km", width = 1.5.dp, color = Colors.Positive
            ) {
                MapUtils.circle(LatLng(projectLat, projectLon), 1000) {
                    MapUtils.latLngToXY(this).toPair()
                }?.let { list -> addPoints(list) }
            }
        } catch (_: Throwable) { }
        // 2 km ring
        try {
            mapState.addPath(
                id = "ring_2km", width = 1.5.dp, color = Colors.Amber
            ) {
                MapUtils.circle(LatLng(projectLat, projectLon), 2000) {
                    MapUtils.latLngToXY(this).toPair()
                }?.let { list -> addPoints(list) }
            }
        } catch (_: Throwable) { }
        // Impact dots: same list as the PDF map (all impacts within 2 km, top impacts otherwise)
        impacts.forEachIndexed { i, dot ->
            try {
                val xy = MapUtils.latLngToXY(dot.lat, dot.lon)
                val c = if (dot.isPositive) Colors.Positive else Colors.Negative
                mapState.addMarker("impact_$i", xy.x, xy.y) {
                    Canvas(Modifier.size(10.dp)) {
                        drawCircle(c, radius = size.width / 2, alpha = 0.8f)
                    }
                }
            } catch (_: Throwable) { }
        }
        // White project picker at center
        try {
            mapState.addMarker("center", center.x, center.y) {
                Box(
                    Modifier.size(12.dp)
                        .background(Color.White, CircleShape)
                        .border(2.dp, Colors.Primary, CircleShape)
                )
            }
        } catch (_: Throwable) { }
    }
    SectionCard {
        Column(
            Modifier.fillMaxWidth().padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            ReportPageHeader(text.plain(text.map.header))
            ReportSectionTitle(text.plain(text.map.title))
            ReportBody(text.plain(text.map.description))
            FullscreenMapContainer(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(mapSizeDp)
                    .clip(RoundedCornerShape(10.dp)),
                controls = {
                    MapZoomControls(
                        mapState = mapState,
                        onRecenter = {
                            scope.launch {
                                mapState.scale = initialScale
                                mapState.snapScrollTo(center.x, center.y)
                            }
                        },
                    )
                },
                fullscreenOverlay = {
                    Box(
                        Modifier.align(Alignment.BottomCenter).padding(16.dp)
                            .clip(RoundedCornerShape(20.dp))
                            .background(Color.White.copy(alpha = 0.92f))
                            .padding(horizontal = 16.dp, vertical = 8.dp)
                    ) {
                        ImpactMapLegend()
                    }
                },
            ) {
                // The map state is shared: it is drawn inline or in the full screen dialog, never both
                MapUI(
                    modifier = Modifier.fillMaxSize(),
                    state = mapState,
                )
            }
            ImpactMapLegend()
            KeraunicMapCounts(data, text)
        }
    }
}

@Composable
private fun ImpactMapLegend() {
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Canvas(Modifier.size(10.dp)) { drawCircle(Colors.Positive, 5f) }
        Text(" CG+ ", fontSize = 11.sp, color = Colors.Text)
        Spacer(Modifier.width(16.dp))
        Canvas(Modifier.size(10.dp)) { drawCircle(Colors.Negative, 5f) }
        Text(" CG\u2212 ", fontSize = 11.sp, color = Colors.Text)
    }
}

@Composable
private fun SectionCard(content: @Composable () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(12.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) { content() }
}

@Composable
private fun SectionTitle(title: String) {
    Text(title, fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Colors.Text)
    HorizontalDivider(color = Colors.Card, thickness = 1.dp)
}
